import { Box, Button, Container, Stack, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";

const needs = [
  ["Celebration packaging", "Boxes that make every occasion feel considered."],
  ["Retail & gifting", "Premium presentation for products that deserve attention."],
  ["Brand-ready packs", "A polished, business-ready solution for your products."],
  ["Made for your brand", "Custom sizes, finishes, prints and quantities."],
];

const strengths = [
  [WorkspacePremiumOutlinedIcon, "Made to present beautifully", "Thoughtful packaging that helps products look their best."],
  [PaletteOutlinedIcon, "Custom solutions", "Packaging tailored to your size, material, and printing needs."],
  [Inventory2OutlinedIcon, "Business-ready quantities", "From a practical order to a larger packaging requirement."],
  [LocalShippingOutlinedIcon, "Helpful local support", "Speak directly with our team over WhatsApp."],
];

const whatsappLink = "https://wa.me/918489811444?text=Hello%20Utsav%20Pack%2C%20I%20would%20like%20help%20with%20custom%20packaging.";

export function UtsavHero() {
  return (
    <Box component="section" className="soft-luxury-glow" sx={{ overflow: "hidden", bgcolor: "linear-gradient(180deg, #FFFDF9 0%, #FFF8EC 100%)", borderBottom: "1px solid #F0E9DF" }}>
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 2.5 }}>
          <Box sx={{ maxWidth: { md: "720px" } }}>
            <Typography sx={{ color: "primary.main", fontWeight: 800, fontSize: "11px", letterSpacing: "0.12em", textTransform: "uppercase", mb: 1.25 }}>
              Thoughtful packaging, made simple
            </Typography>
            <Typography component="h2" sx={{ color: "text.primary", fontSize: { xs: "28px", md: "40px" }, lineHeight: 1.12, letterSpacing: "-0.04em", fontWeight: 800, maxWidth: "640px" }}>
              Packaging that makes every product feel like an occasion.
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ width: { xs: "100%", md: "auto" } }}>
            <Button component="a" href={whatsappLink} target="_blank" rel="noreferrer" variant="contained" endIcon={<ArrowForwardRoundedIcon />} sx={{ px: 2.5, py: 1.25, bgcolor: "primary.main", minWidth: { xs: "100%", sm: "auto" }, "&:hover": { bgcolor: "primary.dark" } }}>
              Custom order
            </Button>
            <Button href="#products" variant="outlined" sx={{ px: 2.5, py: 1.25, borderColor: "#E3D8C3", color: "text.primary", minWidth: { xs: "100%", sm: "auto" }, "&:hover": { borderColor: "primary.main", bgcolor: "#FFF9EE" } }}>
              Browse packaging
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

export function UtsavNeeds() {
  return (
    <Container component="section" maxWidth="xl" sx={{ py: { xs: 7, md: 11 } }}>
      <Typography sx={{ color: "primary.main", fontWeight: 800, fontSize: "12px", letterSpacing: "0.15em", textTransform: "uppercase", mb: 1 }}>Browse by need</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "flex-end" }, gap: 2, mb: 4, flexDirection: { xs: "column", sm: "row" } }}>
        <Typography component="h2" sx={{ fontSize: { xs: "30px", md: "42px" }, lineHeight: 1.1, letterSpacing: "-0.03em", fontWeight: 800, color: "text.primary" }}>Packaging for every kind of celebration.</Typography>
        <Typography sx={{ maxWidth: "350px", color: "text.secondary", lineHeight: 1.6 }}>Choose from ready-to-order packaging or speak to us for a custom solution.</Typography>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, borderTop: "1px solid #E7E6E0", borderLeft: "1px solid #E7E6E0" }}>
        {needs.map(([title, description], index) => (
          <Box key={title} sx={{ minHeight: 215, p: 3.25, borderRight: "1px solid #E7E6E0", borderBottom: "1px solid #E7E6E0", bgcolor: index % 2 ? "#FBFAF7" : "#FFFFFF", transition: "0.25s ease", "&:hover": { bgcolor: "#FFF9EA", transform: "translateY(-4px)" } }}>
            <Typography sx={{ color: "primary.main", fontSize: "13px", fontWeight: 800, mb: 4 }}>0{index + 1}</Typography>
            <Typography sx={{ fontWeight: 750, fontSize: "20px", color: "text.primary", mb: 1 }}>{title}</Typography>
            <Typography sx={{ color: "text.secondary", lineHeight: 1.65, fontSize: "14px" }}>{description}</Typography>
          </Box>
        ))}
      </Box>
    </Container>
  );
}

export function UtsavTrust() {
  return (
    <Box component="section" sx={{ bgcolor: "#F5F5F0", borderTop: "1px solid #ECEBE5", borderBottom: "1px solid #ECEBE5" }}>
      <Container maxWidth="xl" sx={{ py: { xs: 7, md: 10 } }}>
        <Typography sx={{ color: "primary.main", fontWeight: 800, fontSize: "12px", letterSpacing: "0.15em", textTransform: "uppercase", mb: 1 }}>Why Utsav Pack</Typography>
        <Typography component="h2" sx={{ fontWeight: 800, color: "text.primary", fontSize: { xs: "30px", md: "42px" }, letterSpacing: "-0.03em", maxWidth: "650px", lineHeight: 1.12, mb: 5 }}>Designed around the details that make packaging work harder.</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
          {strengths.map(([Icon, title, description]) => (
            <Box key={title} sx={{ bgcolor: "#FFFFFF", p: 3, borderRadius: "16px", border: "1px solid #EAE9E3" }}>
              <Box sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: "#FBF1D8", display: "grid", placeItems: "center", mb: 3 }}><Icon sx={{ color: "primary.main" }} /></Box>
              <Typography sx={{ fontSize: "17px", fontWeight: 750, color: "text.primary", mb: 1 }}>{title}</Typography>
              <Typography sx={{ fontSize: "14px", lineHeight: 1.65, color: "text.secondary" }}>{description}</Typography>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export function UtsavCustomOrder() {
  return (
    <Container component="section" maxWidth="xl" sx={{ py: { xs: 7, md: 11 } }}>
      <Box sx={{ bgcolor: "#B57A08", borderRadius: { xs: "20px", md: "28px" }, px: { xs: 3, md: 7 }, py: { xs: 5, md: 7 }, display: "flex", alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", gap: 3, flexDirection: { xs: "column", md: "row" }, overflow: "hidden", position: "relative" }}>
        <Box sx={{ position: "absolute", width: 320, height: 320, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.25)", right: -110, top: -170 }} />
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: "12px", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", mb: 1.5 }}>For businesses & special requirements</Typography>
          <Typography component="h2" sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: { xs: "30px", md: "42px" }, lineHeight: 1.1, letterSpacing: "-0.03em", maxWidth: "650px" }}>Need packaging made for your product?</Typography>
        </Box>
        <Button component="a" href={whatsappLink} target="_blank" rel="noreferrer" variant="contained" endIcon={<ArrowForwardRoundedIcon />} sx={{ position: "relative", zIndex: 1, bgcolor: "#FFFFFF", color: "#7A4F00", px: 3, py: 1.5, whiteSpace: "nowrap", "&:hover": { bgcolor: "#FFF7E5" } }}>Talk to Utsav Pack</Button>
      </Box>
    </Container>
  );
}
